import { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { hideSheetRowsAndCols } from '../../../../../api/sheetApi';
import ReactNotifications, { showApiErrors } from '../../../../common/reactNotifications/ReactNotifications';
import { sucessState200 } from '../../../../../utils/constants/utilsConstants';

const HideRowsAndColsModal = ({ isOpen, onClose, sheetId }) => {
    const [rowRange, setRowRange] = useState('');
    const [colRange, setColRange] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [unhiding, setUnhiding] = useState(false);

    const columnToNumber = (col) => {
        let result = 0;
        for (let i = 0; i < col.length; i++) {
            result = result * 26 + (col.charCodeAt(i) - 64);
        }
        return result;
    };

    const validate = () => {
        const newErrors = {};

        if (rowRange) {
            if (!/^\d+(-\d+)?$/.test(rowRange)) {
                newErrors.rowRange = 'Invalid format. Use "5" or "5-10".';
            } else {
                const parts = rowRange.split('-');
                const startRow = parseInt(parts[0]);
                if (startRow === 1) {
                    newErrors.rowRange = 'Hiding the first row (header) is not allowed.';
                } else if (parts.length === 2 && startRow >= parseInt(parts[1])) {
                    newErrors.rowRange = 'Start row must be less than end row.';
                }
            }
        }

        if (colRange) {
            if (!/^[A-Z]+(-[A-Z]+)?$/i.test(colRange)) {
                newErrors.colRange = 'Invalid format. Use "C" or "C-F".';
            } else {
                const parts = colRange.toUpperCase().split('-');
                if (parts.length === 2) {
                    const start = columnToNumber(parts[0]);
                    const end = columnToNumber(parts[1]);
                    if (start >= end) {
                        newErrors.colRange = 'Start column must be less than end column.';
                    }
                }
            }
        }

        if (!rowRange && !colRange) {
            newErrors.general = 'Please specify at least one row or column range.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            return;
        }
        setLoading(true);

        const hiddenRanges = {};

        if (rowRange) {
            const parts = rowRange.split('-');
            if (parts.length === 2) {
                hiddenRanges.rows = {
                    start: parseInt(parts[0]),
                    end: parseInt(parts[1])
                };
            } else {
                const row = parseInt(parts[0]);
                hiddenRanges.rows = {
                    start: row,
                    end: row
                };
            }
        }

        if (colRange) {
            const parts = colRange.toUpperCase().split('-');
            if (parts.length === 2) {
                hiddenRanges.cols = {
                    start: columnToNumber(parts[0]),
                    end: columnToNumber(parts[1])
                };
            } else {
                const col = columnToNumber(parts[0]);
                hiddenRanges.cols = {
                    start: col,
                    end: col
                };
            }
        }

        const payload = {
            hidden_ranges: hiddenRanges
        };

        hideSheetRowsAndCols(sheetId, payload, (response) => {
            if (response?.status === 200) {
                ReactNotifications("Sheet protection updated successfully", sucessState200);
                setRowRange('');
                setColRange('');
                setErrors({});
                onClose();
            } else {
                showApiErrors(response);
            }
            setLoading(false);
        });
    };

    const handleUnhideAll = () => {
        setUnhiding(true);
        const payload = {
            hidden_ranges: {}
        };
        hideSheetRowsAndCols(sheetId, payload, (response) => {
            if (response?.status === 200) {
                ReactNotifications("All cells have been unhidden.", sucessState200);
                onClose();
            } else {
                showApiErrors(response);
            }
            setUnhiding(false);
        });
    };

    const handleModalClose = () => {
        setRowRange('');
        setColRange('');
        setErrors({});
        onClose(); // Call the parent's onClose to close the modal
    };

    return (
        <Modal show={isOpen} onHide={handleModalClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    Hide Rows and Columns
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errors.general && (
                    <div className="alert alert-danger" role="alert">
                        {errors.general}
                    </div>
                )}
                <div className="mb-3">
                    <label className="form-label">Row Range</label>
                    <input
                        type="text"
                        className={`form-control ${errors.rowRange ? 'is-invalid' : ''}`}
                        value={rowRange}
                        onChange={(e) => setRowRange(e.target.value)}
                        placeholder="e.g., 5 or 5-10"
                    />
                    {errors.rowRange && <div className="invalid-feedback">{errors.rowRange}</div>}
                </div>
                <div className="mb-3">
                    <label className="form-label">Column Range</label>
                    <input
                        type="text"
                        className={`form-control ${errors.colRange ? 'is-invalid' : ''}`}
                        value={colRange}
                        onChange={(e) => setColRange(e.target.value)}
                        placeholder="e.g., C or C-F"
                    />
                    {errors.colRange && <div className="invalid-feedback">{errors.colRange}</div>}
                </div>
            </Modal.Body>
            <Modal.Footer className='flex !gap-1'>
                <Button variant="danger" onClick={handleUnhideAll} disabled={loading || unhiding}>
                    {unhiding ? 'Unhiding...' : 'Unhide All'}
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={loading || unhiding}
                    className="flex items-center gap-2  !bg-[#AEF468] !border-none hover:bg-[#9DE357] text-black font-semibold rounded-lg shadow-lg transition-all duration-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#AEF468] "
                >
                    {loading ? (
                        <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full border-2 border-t-black animate-spin"></div>
                            <span>Hiding...</span>
                        </div>
                    ) : (
                        "Hide"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default HideRowsAndColsModal;